/**
 * Soft cart limit: max countable (non-gift) items in the cart.
 * Intercepts /cart/add and /cart/change so PDP, drawer, and LP ATC share one rule.
 */
(function () {
  const MAX_CART_ITEMS = 20;
  const FREE_PROP_KEYS = ['_free_gift', '_free_product'];

  const originalFetch = window.fetch.bind(window);

  function limitMessage() {
    const template = window.cartStrings && window.cartStrings.cartLimitError;
    if (template) return template.replace('[quantity]', String(MAX_CART_ITEMS));
    return 'Du kannst maximal ' + MAX_CART_ITEMS + ' Artikel in den Warenkorb legen.';
  }

  function isFreeItem(item) {
    if (!item || !item.properties) return false;
    return FREE_PROP_KEYS.some(function (key) {
      return Boolean(item.properties[key]);
    });
  }

  function countableQuantity(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, item) {
      if (isFreeItem(item)) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  function formHasFreeProperty(form) {
    return FREE_PROP_KEYS.some(function (key) {
      return form.has('properties[' + key + ']') || Boolean(form.get('properties[' + key + ']'));
    });
  }

  function errorResponse(message) {
    return new Response(
      JSON.stringify({
        status: 422,
        message: message,
        description: message,
        errors: message,
        item_count: 0,
        items: [],
      }),
      {
        status: 422,
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  function normalizeUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    try {
      return String(input);
    } catch (e) {
      return '';
    }
  }

  function isCartPath(url, suffix) {
    return url.indexOf('/cart/' + suffix) !== -1;
  }

  async function readBody(init) {
    if (!init || init.body == null) return { kind: 'empty' };

    if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
      return { kind: 'form', form: init.body };
    }

    if (typeof init.body === 'string') {
      try {
        return { kind: 'json', data: JSON.parse(init.body) };
      } catch (e) {
        return { kind: 'empty' };
      }
    }

    if (typeof URLSearchParams !== 'undefined' && init.body instanceof URLSearchParams) {
      const form = new FormData();
      init.body.forEach(function (value, key) {
        form.append(key, value);
      });
      return { kind: 'form', form: form };
    }

    return { kind: 'empty' };
  }

  function requestedAddQuantity(parsed) {
    if (parsed.kind === 'form') {
      if (formHasFreeProperty(parsed.form)) return 0;
      return Math.max(1, Number(parsed.form.get('quantity')) || 1);
    }

    if (parsed.kind === 'json') {
      const data = parsed.data;
      if (Array.isArray(data.items)) {
        return data.items.reduce(function (sum, item) {
          if (isFreeItem(item)) return sum;
          return sum + Math.max(1, Number(item.quantity) || 1);
        }, 0);
      }
      if (isFreeItem(data)) return 0;
      return Math.max(1, Number(data.quantity) || 1);
    }

    return 1;
  }

  function findChangeTarget(cart, lineIndex, variantKey) {
    if (lineIndex != null && !Number.isNaN(lineIndex) && cart.items[lineIndex]) {
      return cart.items[lineIndex];
    }
    if (!variantKey) return null;
    return (
      cart.items.find(function (item) {
        return (
          String(item.key) === variantKey ||
          String(item.id) === variantKey ||
          String(item.variant_id) === variantKey
        );
      }) || null
    );
  }

  function projectedChangeQuantity(cart, parsed) {
    const current = countableQuantity(cart);
    let lineIndex = null;
    let variantKey = null;
    let newQty = null;

    if (parsed.kind === 'json') {
      const data = parsed.data;
      if (data.line != null) lineIndex = Number(data.line) - 1;
      if (data.id != null) variantKey = String(data.id);
      if (data.quantity != null) newQty = Number(data.quantity);
    } else if (parsed.kind === 'form') {
      const form = parsed.form;
      if (form.get('line') != null) lineIndex = Number(form.get('line')) - 1;
      if (form.get('id') != null) variantKey = String(form.get('id'));
      if (form.get('quantity') != null) newQty = Number(form.get('quantity'));
    }

    if (newQty == null || Number.isNaN(newQty)) return current;

    const target = findChangeTarget(cart, lineIndex, variantKey);
    if (!target || isFreeItem(target)) return current;

    const oldQty = Number(target.quantity) || 0;
    if (newQty <= 0) return Math.max(0, current - oldQty);
    return current - oldQty + newQty;
  }

  async function fetchCart() {
    const response = await originalFetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('cart fetch failed');
    return response.json();
  }

  window.themeCartLimit = {
    maxItems: MAX_CART_ITEMS,
    message: limitMessage,
    countableQuantity: countableQuantity,
  };

  window.fetch = async function (input, init) {
    const url = normalizeUrl(input);
    const isAdd = isCartPath(url, 'add');
    const isChange = isCartPath(url, 'change');

    if (!isAdd && !isChange) {
      return originalFetch(input, init);
    }

    try {
      const parsed = await readBody(init || {});
      const cart = await fetchCart();

      if (isAdd) {
        const addQty = requestedAddQuantity(parsed);
        if (addQty > 0 && countableQuantity(cart) + addQty > MAX_CART_ITEMS) {
          return errorResponse(limitMessage());
        }
      }

      if (isChange) {
        const projected = projectedChangeQuantity(cart, parsed);
        if (projected > MAX_CART_ITEMS) {
          return errorResponse(limitMessage());
        }
      }
    } catch (e) {
      // Fail open if cart cannot be read — do not soft-lock checkout flows
      console.warn('[cart-limit]', e);
    }

    return originalFetch(input, init);
  };
})();
